import { isCleanInput } from '@/utils/cleanInput';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { numberComma } from '@diritto/common/utils/string';
import styled from '@emotion/styled';
import { ButtonSize, ButtonType } from 'constants/button';
import { ShortcutEmojis } from 'constants/emoji';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ChangeEvent, FocusEvent, useCallback, useMemo, useRef, useState } from 'react';
import { Button, StyledButton } from 'ui-components/common/Button';
import { TextArea } from 'ui-components/common/TextArea';
import { showToast } from 'utils/modal';

interface DirittoMessageInputProps {
  className?: string;
  initialValue?: string;
  isLoading?: boolean;
  onRegister: (value: string) => void;
  onCancelWrite: () => void;
  ctxString?: string;
  placeholder?: string;
  confirmButtonLabel?: string;
  maxLength: number;
}

export const DirittoMessageInput = ({
  className,
  initialValue = '',
  isLoading,
  onRegister,
  onCancelWrite,
  ctxString,
  placeholder,
  confirmButtonLabel = '등록',
  maxLength,
}: DirittoMessageInputProps) => {
  const { tracker } = useTrackingState();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState(initialValue);
  const count = value?.length || 0;

  const placeholderInitial = useMemo(() => placeholder, [placeholder]);

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value), []);

  const handleFocus = useCallback((e: FocusEvent<HTMLTextAreaElement>) => {
    e.target.selectionStart = e.target.value.length;
    e.target.scrollTop = e.target.scrollHeight;
  }, []);

  const handleClickRegister = useCallback(() => {
    if (value.length > maxLength) {
      showToast(`${ctxString ? `${ctxString}은 ` : ''}${numberComma(maxLength)}자 이하여야 해요.`);
      return;
    }

    if (!isCleanInput(value)) {
      showToast('사용할 수 없는 문자가 포함되어 있어요.');
      return;
    }

    onRegister(value);
  }, [ctxString, maxLength, onRegister, value]);

  const handelAddEmoji = useCallback(
    (emoji: string) => {
      tracker.track(TrackingEventType.ClickReplyEmojiShortCut, { emoji });

      const start = textareaRef?.current?.selectionStart || 0;
      if (start === null) {
        return;
      }

      textareaRef?.current?.focus();
      textareaRef?.current?.setRangeText(emoji, start, start, 'end');
      setValue(textareaRef?.current?.value || '');
    },
    [tracker],
  );

  return (
    <StyledContainer className={className}>
      <StyledEmojiiShortcutArea>
        {ShortcutEmojis.map(emoji => (
          <button type="button" key={emoji} onClick={() => handelAddEmoji(emoji)}>
            {emoji}
          </button>
        ))}
      </StyledEmojiiShortcutArea>
      <StyledTextArea
        ref={textareaRef}
        autoFocus
        placeholder={placeholderInitial}
        onChange={handleChange}
        onFocus={handleFocus}
        value={value}
      />
      <StyledToolArea>
        <StyledLeftTools>
          <StyledCount>
            <p className={count > maxLength ? 'red' : undefined}>{count}</p>
            <p>&nbsp;/&nbsp;{`${maxLength}`}</p>
          </StyledCount>
        </StyledLeftTools>
        <StyledRightTools>
          <Button
            label="취소"
            size={ButtonSize.XSmall}
            buttonTypography={Typography.XXXSmall}
            buttonType={ButtonType.SecondaryNoBorder}
            onClick={onCancelWrite}
            disabled={isLoading}
          />
          <Button
            label={confirmButtonLabel}
            size={ButtonSize.XSmall}
            buttonTypography={Typography.XXXSmall}
            onClick={handleClickRegister}
            disabled={isLoading || !value || value === initialValue}
          />
        </StyledRightTools>
      </StyledToolArea>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  width: 100%;
  height: 191px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 16px;
  background-color: ${Color.White};

  box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
  border-radius: 16px 16px 0 0;
`;

const StyledEmojiiShortcutArea = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  ${Typography.Large};

  width: 100%;
  padding: 0 16px 16px 16px;
`;

const StyledTextArea = styled(TextArea)`
  border: none;
  flex-grow: 1;
  padding: 0 16px;
  ${Typography.XXSmall};
`;

const StyledToolArea = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 16px;
`;

const StyledLeftTools = styled.div`
  display: flex;
  justify-content: flex-start;

  ${StyledButton} + ${StyledButton} {
    margin-left: 4px;
  }
`;

const StyledCount = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  ${Typography.XXXSmall};
  color: ${Color.Gray500};

  & .red {
    color: ${Color.Red500};
  }
`;

const StyledRightTools = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;

  ${StyledButton} + ${StyledButton} {
    margin-left: 4px;
  }
`;
